import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { NbAuthService, NbAuthJWTToken } from '@nebular/auth';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public loggedInUser: any;
  private handlingInvalidSession = false;
  private sessionCheckTimer: ReturnType<typeof setInterval> | null = null;
  private initialNavigationResolved = false;
  private refreshInProgress = false;
  private lastActivityAt = Date.now();
  private readonly refreshThresholdSeconds = 60 * 60;
  private readonly maxIdleForRefreshMs = 15 * 60 * 1000;

  constructor(private http: HttpClient, private authService: NbAuthService, private router: Router) {
    this.trackUserActivity();
    this.hydrateStoredSession();

    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    ).subscribe(() => {
      this.initialNavigationResolved = true;
      this.ensureValidStoredSession();
    });

    this.authService.onTokenChange().subscribe((token: NbAuthJWTToken) => {
      if (token?.isValid?.()) {
        this.handlingInvalidSession = false;
        const payload = token.getPayload?.() || {};
        this.applySessionPayload(payload);
      } else {
        if (!this.hasAnyStoredToken() || this.isLoginRoute()) {
          return;
        }
        this.clearStoredSession();
        this.redirectToLoginIfNeeded();
      }
    });

    this.sessionCheckTimer = setInterval(() => {
      this.ensureValidStoredSession();
      this.refreshSessionIfNeeded();
    }, 30000);
  }
  private trackUserActivity(): void {
    const markActivity = () => {
      this.lastActivityAt = Date.now();
    };

    ['click', 'keydown', 'touchstart', 'mousemove', 'scroll'].forEach((eventName) => {
      window.addEventListener(eventName, markActivity, { passive: true });
    });

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        markActivity();
      }
    });
  }

  private ensureValidStoredSession(): void {
    if (!this.initialNavigationResolved || this.isLoginRoute()) {
      return;
    }

    if (!this.hasAnyStoredToken()) {
      return;
    }

    if (!this.hasValidStoredToken()) {
      this.clearStoredSession();
      this.redirectToLoginIfNeeded();
    }
  }

  private refreshSessionIfNeeded(): void {
    if (this.refreshInProgress || !this.initialNavigationResolved || this.isLoginRoute()) {
      return;
    }

    const payload = this.getStoredTokenPayload();
    if (!payload || !payload.exp) {
      return;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    const secondsToExpire = Number(payload.exp) - nowSeconds;
    const idleMs = Date.now() - this.lastActivityAt;

    if (secondsToExpire > this.refreshThresholdSeconds || idleMs > this.maxIdleForRefreshMs) {
      return;
    }

    this.refreshInProgress = true;
    this.http.post('/ware/auth/index.php?endpoint=refresh', {}).subscribe({
      next: (response: any) => {
        if (response?.token) {
          this.storeRefreshedToken(response.token);
          this.applySessionPayload(response);
          this.handlingInvalidSession = false;
        }
      },
      error: () => {
        this.refreshInProgress = false;
        // Si falla la renovación, se mantiene la sesión actual hasta que expire realmente.
      },
      complete: () => {
        this.refreshInProgress = false;
      },
    });
  }

  private hasAnyStoredToken(): boolean {
    const rawToken = localStorage.getItem('auth_app_token');
    return typeof rawToken === 'string' && rawToken.trim().length > 0;
  }

  private isLoginRoute(): boolean {
    return this.router.url.startsWith('/auth/login') || this.router.url === '/auth' || this.router.url === '';
  }


  private hydrateStoredSession(): void {
    const payload = this.getStoredTokenPayload();
    if (!payload) {
      return;
    }

    const currentRoles = this.extractNormalizedRoles(this.loggedInUser);
    const currentPrivileges = this.extractNormalizedPrivileges(this.loggedInUser);
    const payloadRoles = this.extractNormalizedRoles(payload);
    const payloadPrivileges = this.extractNormalizedPrivileges(payload);

    if (
      this.loggedInUser
      && currentRoles.join('|') === payloadRoles.join('|')
      && currentPrivileges.join('|') === payloadPrivileges.join('|')
    ) {
      return;
    }

    this.applySessionPayload(payload);
  }

  private applySessionPayload(payload: any): void {
    const normalizedPayload = payload && typeof payload === 'object' ? payload : {};
    this.loggedInUser = normalizedPayload;
    localStorage.setItem('privileges', JSON.stringify(this.extractNormalizedPrivileges(normalizedPayload)));
    localStorage.setItem('roles', JSON.stringify(this.extractNormalizedRoles(normalizedPayload)));
  }

  private getStoredTokenPayload(): any | null {
    const rawToken = localStorage.getItem('auth_app_token');
    if (!rawToken) {
      return null;
    }

    try {
      const tokenData = JSON.parse(rawToken);
      const token = tokenData?.value;
      if (!token || typeof token !== 'string') {
        return null;
      }

      const parts = token.split('.');
      if (parts.length < 2) {
        return null;
      }

      const payloadJson = this.base64UrlDecode(parts[1]);
      const payload = JSON.parse(payloadJson);
      return payload && typeof payload === 'object' ? payload : null;
    } catch (e) {
      return null;
    }
  }

  private hasValidStoredToken(): boolean {
    const rawToken = localStorage.getItem('auth_app_token');
    if (!rawToken) {
      return false;
    }

    try {
      const payload = this.getStoredTokenPayload();
      if (!payload || typeof payload !== 'object') {
        return false;
      }

      if (!payload.exp) {
        return true;
      }

      const now = Math.floor(Date.now() / 1000);
      return Number(payload.exp) > now;
    } catch (e) {
      return false;
    }
  }

  private base64UrlDecode(value: string): string {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=');
    return decodeURIComponent(Array.prototype.map.call(atob(padded), (char: string) => {
      return '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  }

  private storeRefreshedToken(token: string): void {
    const rawToken = localStorage.getItem('auth_app_token');

    try {
      const current = rawToken ? JSON.parse(rawToken) : {};
      const nextToken = current && typeof current === 'object' ? current : {};
      nextToken.value = token;
      localStorage.setItem('auth_app_token', JSON.stringify(nextToken));
      return;
    } catch (e) {
      // Si el formato almacenado no es JSON válido, se reconstruye.
    }

    localStorage.setItem('auth_app_token', JSON.stringify({ value: token }));
  }

  private clearStoredSession(): void {
    this.loggedInUser = null;
    localStorage.removeItem('privileges');
    localStorage.removeItem('roles');
    localStorage.removeItem('auth_app_token');
  }

  private redirectToLoginIfNeeded(): void {
    if (this.handlingInvalidSession || this.isLoginRoute()) {
      return;
    }

    this.handlingInvalidSession = true;

    if (!this.hasAnyStoredToken()) {
      this.router.navigate(['/auth/login']);
      return;
    }

    this.authService.logout('email').subscribe({
      next: () => {
        this.router.navigate(['/auth/login']);
      },
      error: () => {
        this.router.navigate(['/auth/login']);
      },
      complete: () => {
        this.router.navigate(['/auth/login']);
      },
    });
  }


  hasValidSessionSnapshot(): boolean {
    return this.hasValidStoredToken();
  }

  getEffectiveRoles(): string[] {
    return this.getRoles();
  }

  verifyStoredSession(): Observable<any> {
    return this.http.get('/ware/auth/index.php?endpoint=verify');
  }

  refreshStoredSession(): Observable<any> {
    return this.http.post('/ware/auth/index.php?endpoint=refresh', {});
  }

  applyVerifiedSession(payload: any): void {
    this.applySessionPayload(payload);
  }

  private extractNormalizedRoles(source: any): string[] {
    return this.normalizeStoredList(source?.roles ?? source?.role ?? source?.rol ?? source?.perfiles);
  }

  private extractNormalizedPrivileges(source: any): string[] {
    return this.normalizeStoredList(source?.privileges ?? source?.privilegios ?? source?.permissions ?? source?.permisos);
  }

  // Verificar si el usuario está autenticado
  isAuthenticated(): Observable<boolean> {
    return this.authService.isAuthenticated();
  }

  isAuthenticatedOrRefresh(): Observable<boolean> {
    return this.authService.isAuthenticatedOrRefresh();
  }

  getPrivileges(): string[] {
    this.hydrateStoredSession();
    const privileges = localStorage.getItem('privileges');
    if (!privileges) {
      return this.extractNormalizedPrivileges(this.loggedInUser);
    }

    try {
      const parsedPrivileges = JSON.parse(privileges);
      return this.normalizeStoredList(parsedPrivileges);
    } catch (e) {
      return this.normalizeStoredList(privileges);
    }
  }

  getRoles(): string[] {
    this.hydrateStoredSession();
    const roles = localStorage.getItem('roles');
    if (roles) {
      try {
        const parsedRoles = JSON.parse(roles);
        return this.normalizeStoredList(parsedRoles);
      } catch (e) {
        return this.normalizeStoredList(roles);
      }
    }

    return this.extractNormalizedRoles(this.loggedInUser);
  }

getUserId(): string {
  return this.loggedInUser?.user_id || '';
}

  hasPrivilege(privilege: string): boolean {
    return this.getPrivileges().includes(privilege);
  }

  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  isManager(): boolean {
    return this.hasRole('Manager');
  }

  isComercial(): boolean {
    return this.hasRole('Comercial');
  }

  isUsuario(): boolean {
    return this.hasRole('Usuario');
  }

  isUsuarioAvanzado(): boolean {
    return this.hasRole('Usuario Avanzado');
  }

  isUsuarioTransformacion(): boolean {
    return this.hasRole('Usuario Transformación');
  }

  isUsuarioAvanzadoTransformacion(): boolean {
    return this.hasRole('Usuario Avanzado Transformación');
  }

  canAccessTransformacion(): boolean {
    return this.isManager() || this.isUsuarioTransformacion() || this.isUsuarioAvanzadoTransformacion();
  }

  canManageTransformacionSensitiveState(): boolean {
    return this.isManager();
  }

  canManageTransformacionReconciliacion(): boolean {
    return this.isManager() || this.isUsuarioAvanzadoTransformacion();
  }

  logout() {
    this.clearStoredSession();
    this.authService.logout('email');
  }
  logoutUser() {
    this.clearStoredSession();
    this.authService.logout('email');
  }

  private normalizeStoredList(value: any): string[] {
    const normalizeItem = (item: any): string => {
      const raw = String(item || '').trim();
      const key = raw.toLowerCase();
      switch (key) {
        case 'admin':
          return 'Manager';
        case 'user':
          return 'Usuario';
        case 'usuario':
          return 'Usuario';
        case 'usuario avanzado':
        case 'usuario_avanzado':
        case 'advanceduser':
        case 'advanced user':
          return 'Usuario Avanzado';
        case 'usuario transformación':
        case 'usuario transformacion':
        case 'usuario_transformacion':
        case 'transformationuser':
        case 'transformation user':
          return 'Usuario Transformación';
        case 'usuario avanzado transformación':
        case 'usuario avanzado transformacion':
        case 'usuario_avanzado_transformacion':
        case 'advanced_transformacion':
        case 'advanced transformation user':
        case 'advancedtransformationuser':
          return 'Usuario Avanzado Transformación';
        case 'operator':
        case 'operador':
          return 'Operador';
        case 'commercial':
        case 'comercial':
          return 'Comercial';
        case 'manager':
          return 'Manager';
        default:
          return raw;
      }
    };

    if (Array.isArray(value)) {
      return value.map((item) => normalizeItem(item)).filter(Boolean);
    }

    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.map((item) => normalizeItem(item)).filter(Boolean);
        }
      } catch (e) {
        // Puede llegar como texto plano, CSV o array de PostgreSQL.
      }

      const normalized = value.trim();
      if (!normalized) {
        return [];
      }

      if (normalized.startsWith('{') && normalized.endsWith('}')) {
        return normalized
          .slice(1, -1)
          .split(',')
          .map((item) => normalizeItem(item.replace(/^"|"$/g, '')))
          .filter(Boolean);
      }

      return normalized.split(',').map((item) => normalizeItem(item)).filter(Boolean);
    }

    return [];
  }

}


/*


import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NbAuthService, NbAuthJWTToken } from '@nebular/auth';

@Injectable({
  providedIn: 'root',
})
export class AuthService {

    public loggedInUser;
  constructor(private authService: NbAuthService) {
    this.authService.onTokenChange().subscribe((token: NbAuthJWTToken) => {
        if (token.isValid()) {
          // Si el token es válido, almacénalo
          localStorage.setItem('privileges', JSON.stringify(token.getPayload().privileges));
        } else {
          // Redirigir si el token no es válido
          this.authService.logout('email');
        }
      });
  }

  getPrivileges(): string[] {
    const privileges = localStorage.getItem('privileges');
    return privileges ? JSON.parse(privileges) : [];
  }

  logout() {
    localStorage.removeItem('privileges'); // Borrar privilegios al cerrar sesión
  }
  logoutUser() {
    localStorage.removeItem('privileges'); // Borrar privilegios al cerrar sesión
  }
}*/
